import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import {
  Circle,
  Contrast,
  Feather,
  Moon,
  Sun,
  Zap,
  type LucideIcon,
} from "lucide-react";

import type { ThemeMode, ThemePalette } from "@/types/users";

/**
 * Theme is two orthogonal axes, each a named variant applied as its own class
 * on `<html>`:
 *
 * - **mode** (`.light` class; dark is the unclassed default): the light/dark
 *   tone of the UI.
 * - **palette** (`.palette-<name>` class; `default` is unclassed): a named
 *   complete look. `default` is the brand-colored outlet look (parchment +
 *   deep teal + editorial type). `mono` is the neutral monochrome look;
 *   `classic` is the legacy PowerUp ramp; `modern` is the prior-default
 *   Tailwind-ish ramp. New palettes drop in as a registry entry + a CSS block
 *   — see `PALETTE_VARIANTS` below and `docs/DESIGN-SYSTEM.md` § "Theme model".
 *
 * **Where the choice is stored.** A signed-in user's mode and palette are
 * server-side preferences (`session.preferences.theme_mode` /
 * `theme_palette`, set from Preferences); `AppShell` applies them through
 * `applyThemeClasses`. The public presentation pages have no session, so
 * their toggle (`useTheme`) keeps the choice in this browser. Either way the
 * `theme` localStorage key caches the last look applied, which the pre-paint
 * script in `index.html` reads so a reload never flashes. It is a cache of
 * whoever was on screen, not anyone's choice: nothing adopts it.
 *
 * The older `mode` and `palette` keys are the choice a browser made before
 * preferences moved to the server. Nothing writes them now; they are read
 * once, adopted for the first user to sign in (`useAdoptBrowserPreferences`),
 * and deleted.
 *
 * The two classes are the single source of truth — every token-backed
 * utility resolves off them, so no `dark:` / palette variants are needed at
 * call sites.
 */

export type Mode = ThemeMode;

export type PaletteId = ThemePalette;

export interface ModeVariant {
  id: Mode;
  label: string;
  icon: LucideIcon;
}

export interface PaletteVariant {
  id: PaletteId;
  label: string;
  /** What the look is, in a few words — shown under its preview. */
  description: string;
  icon: LucideIcon;
  /** Class applied to `<html>`. `null` for the default (unclassed) palette. */
  className: string | null;
}

/** Registry — the pickers render straight off these. Order = display order. */
export const MODE_VARIANTS: readonly ModeVariant[] = [
  { id: "light", label: "Light", icon: Sun },
  { id: "dark", label: "Dark", icon: Moon },
];

export const PALETTE_VARIANTS: readonly PaletteVariant[] = [
  // Default is the base palette — editorial parchment + deep teal +
  // editorial type, modeled on the Outlet energy-desk reference. No
  // class needed; an unclassed `<html>` falls through to this look,
  // which is also what `:root` / `.light` in `index.css` define.
  {
    id: "default",
    label: "Default",
    description: "Editorial parchment and deep teal",
    icon: Feather,
    className: null,
  },
  // Classic — legacy PowerUp ramp; the prior fresh-install default.
  {
    id: "classic",
    label: "Classic",
    description: "The original PowerUp navy and mint",
    icon: Zap,
    className: "palette-classic",
  },
  // Modern — Tailwind-ish dark-first ramp that used to be unclassed.
  // Now an opt-in override.
  {
    id: "modern",
    label: "Modern",
    description: "Deep green with a bright teal accent",
    icon: Circle,
    className: "palette-modern",
  },
  // Monochrome — neutral grey chrome; brand accents ride through.
  {
    id: "mono",
    label: "Monochrome",
    description: "Neutral greys; brand accents stay",
    icon: Contrast,
    className: "palette-mono",
  },
];

/** The look a user who has never chosen gets. */
export const DEFAULT_MODE: Mode = "light";
export const DEFAULT_PALETTE: PaletteId = "default";

const MODE_IDS = new Set<string>(MODE_VARIANTS.map((m) => m.id));
const PALETTE_IDS = new Set<string>(PALETTE_VARIANTS.map((p) => p.id));
const ALL_PALETTE_CLASSES = PALETTE_VARIANTS.map((p) => p.className).filter(
  (c): c is string => c != null,
);

/** The classes an element wears to take a look — `<html>`, or a preview. */
export function themeClassNames(mode: Mode, palette: PaletteId): string[] {
  const paletteClass = PALETTE_VARIANTS.find((p) => p.id === palette)?.className;
  // A preview of the dark default needs `.dark` to reset what it inherits
  // from a light page; `<html>` itself is dark when unclassed.
  return [mode === "light" ? "light" : "dark", ...(paletteClass ? [paletteClass] : [])];
}

/** The pre-paint cache of the last look applied in this browser. */
const THEME_CACHE_KEY = "theme";

/**
 * Puts a look on `<html>` — exactly one palette class, `.light` or not — and
 * caches it for the pre-paint script.
 */
export function applyThemeClasses(mode: Mode, palette: PaletteId): void {
  const el = document.documentElement;
  el.classList.toggle("light", mode === "light");
  const active = PALETTE_VARIANTS.find((p) => p.id === palette)?.className;
  // Swap exactly one palette class on, the rest off — so switching between
  // named palettes never stacks two override layers.
  for (const cls of ALL_PALETTE_CLASSES) {
    el.classList.toggle(cls, cls === active);
  }
  try {
    localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({ mode, palette }));
  } catch {
    // ignore: private-mode storage failures
  }
}

/** The last look applied in this browser, or `null` if none was cached. */
export function readThemeCache(): { mode: Mode; palette: PaletteId } | null {
  try {
    const raw = JSON.parse(localStorage.getItem(THEME_CACHE_KEY) ?? "null") as {
      mode?: unknown;
      palette?: unknown;
    } | null;
    const mode = typeof raw?.mode === "string" && MODE_IDS.has(raw.mode) ? (raw.mode as Mode) : null;
    const palette =
      typeof raw?.palette === "string" && PALETTE_IDS.has(raw.palette) ? (raw.palette as PaletteId) : null;
    return mode && palette ? { mode, palette } : null;
  } catch {
    return null;
  }
}

/**
 * The mode this browser chose before preferences moved to the server, or
 * `null`. Legacy: nothing writes the key now.
 */
export function readStoredMode(): Mode | null {
  try {
    const raw = localStorage.getItem("mode");
    return raw && MODE_IDS.has(raw) ? (raw as Mode) : null;
  } catch {
    return null;
  }
}

/**
 * The palette this browser chose before preferences moved to the server, or
 * `null`. Legacy: nothing writes the key now.
 */
export function readStoredPalette(): PaletteId | null {
  try {
    const raw = localStorage.getItem("palette");
    return raw && PALETTE_IDS.has(raw) ? (raw as PaletteId) : null;
  } catch {
    return null;
  }
}

function subscribeToHtmlClass(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  return () => observer.disconnect();
}

function currentMode(): Mode {
  return document.documentElement.classList.contains("light") ? "light" : "dark";
}

/**
 * The mode the page is showing, read off `<html>` — for chrome that must match
 * it (the toast surface) wherever the choice came from, session or not.
 */
export function useThemeMode(): Mode {
  return useSyncExternalStore(subscribeToHtmlClass, currentMode, () => DEFAULT_MODE);
}

/**
 * A theme kept in this browser only — for the public presentation pages,
 * which have no session. Signed-in screens use the server-side preferences
 * (`useUserPreferences`) instead.
 */
export function useTheme() {
  const [mode, setModeState] = useState<Mode>(
    () => readThemeCache()?.mode ?? readStoredMode() ?? DEFAULT_MODE,
  );
  const [palette, setPaletteState] = useState<PaletteId>(
    () => readThemeCache()?.palette ?? readStoredPalette() ?? DEFAULT_PALETTE,
  );

  useEffect(() => {
    applyThemeClasses(mode, palette);
  }, [mode, palette]);

  const setMode = useCallback((next: Mode) => setModeState(next), []);
  const setPalette = useCallback((next: PaletteId) => setPaletteState(next), []);

  return { mode, setMode, palette, setPalette };
}
